import React, { useState } from 'react';
import { useTheme } from '@mui/material/styles';
import {
    Box,
    Card,
    CardContent,
    Grid,
    Typography,
    Button,
    TextField,
    IconButton,
    Chip,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Tabs,
    Tab,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Alert,
    Divider,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    Collapse,
    LinearProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    ToggleButton,
    ToggleButtonGroup
} from '@mui/material';
// CodeMirror imports removed - use TextField instead

// icons
import {
    IconDatabase,
    IconTable,
    IconPlayerPlay,
    IconDownload,
    IconDeviceFloppy,
    IconChevronRight,
    IconChevronDown,
    IconSortAscending,
    IconCode,
    IconCopy,
    IconShare,
    IconHistory,
    IconEdit
} from '@tabler/icons-react';

const QueryBuilder = () => {
    const theme = useTheme();
    const [tabValue, setTabValue] = useState(0);
    const [queryType, setQueryType] = useState('sql');
    const [query, setQuery] = useState('SELECT * FROM users LIMIT 10;');
    const [mongoQuery, setMongoQuery] = useState('db.users.find({}).limit(10)');
    const [selectedDatabase, setSelectedDatabase] = useState('production_db');
    const [selectedTable, setSelectedTable] = useState('users');
    const [expandedTables, setExpandedTables] = useState({});
    const [isExecuting, setIsExecuting] = useState(false);
    const [executionTime, setExecutionTime] = useState(null);
    const [saveDialog, setSaveDialog] = useState(false);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    // Mock database schema
    const databases = [
        {
            name: 'production_db',
            type: 'PostgreSQL',
            tables: [
                { name: 'users', columns: ['id', 'name', 'email', 'created_at', 'updated_at'] },
                { name: 'orders', columns: ['id', 'user_id', 'total', 'status', 'created_at'] },
                { name: 'products', columns: ['id', 'name', 'price', 'category', 'stock'] }
            ]
        },
        {
            name: 'analytics_db',
            type: 'MongoDB',
            collections: [
                { name: 'events', fields: ['_id', 'user_id', 'event_type', 'timestamp', 'properties'] },
                { name: 'sessions', fields: ['_id', 'user_id', 'start_time', 'duration', 'page_views'] }
            ]
        }
    ];

    // Mock query results
    const queryResults = {
        columns: ['id', 'name', 'email', 'created_at'],
        rows: [
            { id: 1, name: 'John Doe', email: 'john@example.com', created_at: '2024-01-01' },
            { id: 2, name: 'Jane Smith', email: 'jane@example.com', created_at: '2024-01-02' },
            { id: 3, name: 'Bob Johnson', email: 'bob@example.com', created_at: '2024-01-03' },
            { id: 4, name: 'Alice Brown', email: 'alice@example.com', created_at: '2024-01-04' },
            { id: 5, name: 'Charlie Wilson', email: 'charlie@example.com', created_at: '2024-01-05' }
        ]
    };

    // Query history
    const queryHistory = [
        { id: 1, query: 'SELECT COUNT(*) FROM users WHERE created_at > NOW() - INTERVAL 7 DAY', time: '2 mins ago', duration: '0.23s' },
        { id: 2, query: 'db.orders.aggregate([{$group: {_id: "$status", count: {$sum: 1}}}])', time: '15 mins ago', duration: '0.45s' },
        { id: 3, query: 'UPDATE products SET price = price * 1.1 WHERE category = "electronics"', time: '1 hour ago', duration: '1.2s' }
    ];

    // Saved queries
    const savedQueries = [
        {
            id: 1,
            name: 'Active Users Report',
            query: 'SELECT * FROM users WHERE last_login > NOW() - INTERVAL 30 DAY',
            database: 'production_db'
        },
        {
            id: 2,
            name: 'Revenue by Category',
            query: 'SELECT category, SUM(total) FROM orders GROUP BY category',
            database: 'production_db'
        },
        { id: 3, name: 'User Sessions Analysis', query: 'db.sessions.aggregate([...])', database: 'analytics_db' }
    ];

    // Query templates
    const queryTemplates = [
        {
            category: 'Select',
            templates: ['SELECT * FROM table', 'SELECT column1, column2 FROM table', 'SELECT DISTINCT column FROM table']
        },
        { category: 'Join', templates: ['SELECT * FROM t1 JOIN t2 ON t1.id = t2.t1_id', 'LEFT JOIN', 'RIGHT JOIN'] },
        { category: 'Aggregate', templates: ['SELECT COUNT(*) FROM table', 'SELECT AVG(column) FROM table', 'GROUP BY column'] },
        { category: 'Filter', templates: ['WHERE column = value', 'WHERE column LIKE "%pattern%"', 'WHERE column IN (1,2,3)'] }
    ];

    const handleExecuteQuery = () => {
        setIsExecuting(true);
        setTimeout(() => {
            setIsExecuting(false);
            setExecutionTime(Math.random() * 2);
        }, 1500);
    };

    const handleTableExpand = (table) => {
        setExpandedTables((prev) => ({
            ...prev,
            [table]: !prev[table]
        }));
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    return (
        <Box>
            <Grid container spacing={3}>
                {/* Left Panel - Schema Browser */}
                <Grid item xs={12} md={3}>
                    <Card>
                        <CardContent>
                            <Typography variant="h5" gutterBottom>
                                Database Schema
                            </Typography>
                            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                                <InputLabel>Database</InputLabel>
                                <Select value={selectedDatabase} label="Database" onChange={(e) => setSelectedDatabase(e.target.value)}>
                                    {databases.map((db) => (
                                        <MenuItem key={db.name} value={db.name}>
                                            <Box display="flex" alignItems="center" gap={1}>
                                                <IconDatabase size={16} />
                                                {db.name}
                                                <Chip label={db.type} size="small" />
                                            </Box>
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            <List dense>
                                {databases
                                    .find((db) => db.name === selectedDatabase)
                                    ?.tables?.map((table) => (
                                        <Box key={table.name}>
                                            <ListItem button onClick={() => handleTableExpand(table.name)}>
                                                <ListItemIcon>
                                                    {expandedTables[table.name] ? (
                                                        <IconChevronDown size={18} />
                                                    ) : (
                                                        <IconChevronRight size={18} />
                                                    )}
                                                </ListItemIcon>
                                                <ListItemIcon>
                                                    <IconTable size={18} />
                                                </ListItemIcon>
                                                <ListItemText primary={table.name} />
                                            </ListItem>
                                            <Collapse in={expandedTables[table.name]} timeout="auto" unmountOnExit>
                                                <List component="div" disablePadding>
                                                    {table.columns?.map((column) => (
                                                        <ListItem key={column} sx={{ pl: 8 }}>
                                                            <ListItemText primary={column} primaryTypographyProps={{ variant: 'body2' }} />
                                                        </ListItem>
                                                    ))}
                                                </List>
                                            </Collapse>
                                        </Box>
                                    ))}
                                {databases
                                    .find((db) => db.name === selectedDatabase)
                                    ?.collections?.map((collection) => (
                                        <Box key={collection.name}>
                                            <ListItem button onClick={() => handleTableExpand(collection.name)}>
                                                <ListItemIcon>
                                                    {expandedTables[collection.name] ? (
                                                        <IconChevronDown size={18} />
                                                    ) : (
                                                        <IconChevronRight size={18} />
                                                    )}
                                                </ListItemIcon>
                                                <ListItemIcon>
                                                    <IconTable size={18} />
                                                </ListItemIcon>
                                                <ListItemText primary={collection.name} />
                                            </ListItem>
                                            <Collapse in={expandedTables[collection.name]} timeout="auto" unmountOnExit>
                                                <List component="div" disablePadding>
                                                    {collection.fields?.map((field) => (
                                                        <ListItem key={field} sx={{ pl: 8 }}>
                                                            <ListItemText primary={field} primaryTypographyProps={{ variant: 'body2' }} />
                                                        </ListItem>
                                                    ))}
                                                </List>
                                            </Collapse>
                                        </Box>
                                    ))}
                            </List>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Right Panel - Query Editor and Results */}
                <Grid item xs={12} md={9}>
                    <Card>
                        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                            <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
                                <Tab label="Query Editor" />
                                <Tab label="History" />
                                <Tab label="Saved Queries" />
                                <Tab label="Templates" />
                            </Tabs>
                        </Box>

                        {/* Query Editor Tab */}
                        {tabValue === 0 && (
                            <CardContent>
                                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                                    <ToggleButtonGroup
                                        value={queryType}
                                        exclusive
                                        onChange={(e, newType) => newType && setQueryType(newType)}
                                        size="small"
                                    >
                                        <ToggleButton value="sql">SQL</ToggleButton>
                                        <ToggleButton value="mongodb">MongoDB</ToggleButton>
                                    </ToggleButtonGroup>

                                    <Box display="flex" gap={1}>
                                        <Button
                                            variant="contained"
                                            startIcon={isExecuting ? null : <IconPlayerPlay />}
                                            onClick={handleExecuteQuery}
                                            disabled={isExecuting}
                                        >
                                            {isExecuting ? 'Executing...' : 'Execute'}
                                        </Button>
                                        <Button variant="outlined" startIcon={<IconDeviceFloppy />} onClick={() => setSaveDialog(true)}>
                                            Save
                                        </Button>
                                        <IconButton>
                                            <IconCode />
                                        </IconButton>
                                        <IconButton>
                                            <IconShare />
                                        </IconButton>
                                    </Box>
                                </Box>

                                {/* Code Editor */}
                                <Paper variant="outlined" sx={{ mb: 3, p: 2 }}>
                                    <TextField
                                        fullWidth
                                        multiline
                                        rows={8}
                                        value={queryType === 'sql' ? query : mongoQuery}
                                        onChange={(e) => {
                                            if (queryType === 'sql') {
                                                setQuery(e.target.value);
                                            } else {
                                                setMongoQuery(e.target.value);
                                            }
                                        }}
                                        sx={{
                                            '& .MuiInputBase-root': {
                                                fontFamily: 'monospace',
                                                fontSize: '0.875rem'
                                            }
                                        }}
                                        placeholder={queryType === 'sql' ? 'Enter SQL query...' : 'Enter MongoDB query...'}
                                    />
                                </Paper>

                                {/* Execution Progress */}
                                {isExecuting && <LinearProgress sx={{ mb: 2 }} />}

                                {/* Execution Info */}
                                {executionTime && (
                                    <Alert
                                        severity="success"
                                        sx={{ mb: 2 }}
                                        action={
                                            <Box display="flex" gap={1}>
                                                <Chip label={`${queryResults.rows.length} rows`} size="small" />
                                                <Chip label={`${executionTime.toFixed(2)}s`} size="small" />
                                            </Box>
                                        }
                                    >
                                        Query executed successfully
                                    </Alert>
                                )}

                                {/* Results Table */}
                                <Typography variant="h5" gutterBottom>
                                    Results
                                </Typography>
                                <TableContainer component={Paper} variant="outlined">
                                    <Table>
                                        <TableHead>
                                            <TableRow>
                                                {queryResults.columns.map((column) => (
                                                    <TableCell key={column}>
                                                        <Box display="flex" alignItems="center" gap={1}>
                                                            {column}
                                                            <IconSortAscending size={16} />
                                                        </Box>
                                                    </TableCell>
                                                ))}
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {queryResults.rows
                                                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                                .map((row, index) => (
                                                    <TableRow key={index} hover>
                                                        {queryResults.columns.map((column) => (
                                                            <TableCell key={column}>{row[column]}</TableCell>
                                                        ))}
                                                    </TableRow>
                                                ))}
                                        </TableBody>
                                    </Table>
                                    <TablePagination
                                        rowsPerPageOptions={[5, 10, 25, 100]}
                                        component="div"
                                        count={queryResults.rows.length}
                                        rowsPerPage={rowsPerPage}
                                        page={page}
                                        onPageChange={handleChangePage}
                                        onRowsPerPageChange={handleChangeRowsPerPage}
                                    />
                                </TableContainer>

                                {/* Export Options */}
                                <Box display="flex" justifyContent="flex-end" mt={2} gap={1}>
                                    <Button startIcon={<IconDownload />} size="small">
                                        Export CSV
                                    </Button>
                                    <Button startIcon={<IconDownload />} size="small">
                                        Export JSON
                                    </Button>
                                    <Button startIcon={<IconCopy />} size="small">
                                        Copy Results
                                    </Button>
                                </Box>
                            </CardContent>
                        )}

                        {/* History Tab */}
                        {tabValue === 1 && (
                            <CardContent>
                                <Typography variant="h5" gutterBottom>
                                    Query History
                                </Typography>
                                <List>
                                    {queryHistory.map((item) => (
                                        <React.Fragment key={item.id}>
                                            <ListItem>
                                                <ListItemIcon>
                                                    <IconHistory />
                                                </ListItemIcon>
                                                <ListItemText
                                                    primary={
                                                        <Typography
                                                            variant="body2"
                                                            sx={{
                                                                fontFamily: 'monospace',
                                                                bgcolor: 'grey.100',
                                                                p: 1,
                                                                borderRadius: 1
                                                            }}
                                                        >
                                                            {item.query}
                                                        </Typography>
                                                    }
                                                    secondary={
                                                        <Box display="flex" gap={2} mt={1}>
                                                            <Chip label={item.time} size="small" />
                                                            <Chip label={item.duration} size="small" color="primary" />
                                                        </Box>
                                                    }
                                                />
                                                <Box display="flex" gap={1}>
                                                    <IconButton size="small">
                                                        <IconPlayerPlay size={16} />
                                                    </IconButton>
                                                    <IconButton size="small">
                                                        <IconCopy size={16} />
                                                    </IconButton>
                                                </Box>
                                            </ListItem>
                                            <Divider />
                                        </React.Fragment>
                                    ))}
                                </List>
                            </CardContent>
                        )}

                        {/* Saved Queries Tab */}
                        {tabValue === 2 && (
                            <CardContent>
                                <Typography variant="h5" gutterBottom>
                                    Saved Queries
                                </Typography>
                                <Grid container spacing={2}>
                                    {savedQueries.map((savedQuery) => (
                                        <Grid item xs={12} md={6} key={savedQuery.id}>
                                            <Card variant="outlined">
                                                <CardContent>
                                                    <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                                                        <Box>
                                                            <Typography variant="h6" gutterBottom>
                                                                {savedQuery.name}
                                                            </Typography>
                                                            <Chip label={savedQuery.database} size="small" sx={{ mb: 1 }} />
                                                            <Typography
                                                                variant="body2"
                                                                sx={{
                                                                    fontFamily: 'monospace',
                                                                    bgcolor: 'grey.100',
                                                                    p: 1,
                                                                    borderRadius: 1,
                                                                    fontSize: '0.75rem'
                                                                }}
                                                            >
                                                                {savedQuery.query}
                                                            </Typography>
                                                        </Box>
                                                        <Box display="flex" flexDirection="column" gap={1}>
                                                            <IconButton size="small">
                                                                <IconPlayerPlay size={16} />
                                                            </IconButton>
                                                            <IconButton size="small">
                                                                <IconEdit size={16} />
                                                            </IconButton>
                                                        </Box>
                                                    </Box>
                                                </CardContent>
                                            </Card>
                                        </Grid>
                                    ))}
                                </Grid>
                            </CardContent>
                        )}

                        {/* Templates Tab */}
                        {tabValue === 3 && (
                            <CardContent>
                                <Typography variant="h5" gutterBottom>
                                    Query Templates
                                </Typography>
                                <Grid container spacing={2}>
                                    {queryTemplates.map((category) => (
                                        <Grid item xs={12} md={6} key={category.category}>
                                            <Card variant="outlined">
                                                <CardContent>
                                                    <Typography variant="h6" gutterBottom>
                                                        {category.category}
                                                    </Typography>
                                                    <List dense>
                                                        {category.templates.map((template, index) => (
                                                            <ListItem key={index}>
                                                                <ListItemText
                                                                    primary={
                                                                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                                                            {template}
                                                                        </Typography>
                                                                    }
                                                                />
                                                                <IconButton size="small">
                                                                    <IconCopy size={16} />
                                                                </IconButton>
                                                            </ListItem>
                                                        ))}
                                                    </List>
                                                </CardContent>
                                            </Card>
                                        </Grid>
                                    ))}
                                </Grid>
                            </CardContent>
                        )}
                    </Card>
                </Grid>
            </Grid>

            {/* Save Query Dialog */}
            <Dialog open={saveDialog} onClose={() => setSaveDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Save Query</DialogTitle>
                <DialogContent>
                    <TextField fullWidth label="Query Name" variant="outlined" sx={{ mt: 2, mb: 2 }} />
                    <TextField fullWidth label="Description" variant="outlined" multiline rows={2} />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setSaveDialog(false)}>Cancel</Button>
                    <Button variant="contained" onClick={() => setSaveDialog(false)}>
                        Save Query
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default QueryBuilder;
